'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CheckCircle, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

export default function QualificationSection() {
    const { t } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    
    const idealCandidates = t('qualification.idealCandidates', { returnObjects: true }) as string[];
    const contraindications = t('qualification.contraindications', { returnObjects: true }) as string[];
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states
            gsap.set(titleRef.current, { opacity: 0, y: 50 });
            gsap.set(contentRef.current, { opacity: 0, y: 30 });

            // Title animation with scroll trigger
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Content animation
            gsap.to(contentRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: contentRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="qualification"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className={cn(
                            "text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif",
                            isRTL && "font-arabic"
                        )}
                    >
                        {t('qualification.title.part1')}{' '}
                        <span className="text-gold relative">
                            {t('qualification.title.part2')}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>
                    </h2>
                    <p className={cn(
                        "text-xl text-slate-600 dark:text-slate-300 leading-relaxed",
                        isRTL && "font-arabic"
                    )}>
                        {t('qualification.description')}
                    </p>
                </div>

                {/* Qualification Content */}
                <div ref={contentRef} className="grid lg:grid-cols-2 gap-12">
                    {/* Ideal Candidates */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                        className={cn(
                            "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-8 border border-green-200 dark:border-green-800",
                            isRTL && "text-right"
                        )}
                    >
                        <div className={cn(
                            "relative mb-6",
                            isRTL ? "pr-16" : "pl-16"
                        )}>
                            <div className={cn(
                                "w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center absolute",
                                isRTL ? "right-0" : "left-0"
                            )}>
                                <CheckCircle className="w-6 h-6 text-white" />
                            </div>
                            <h3 className={cn(
                                "text-2xl font-bold text-green-800 dark:text-green-300",
                                isRTL && "font-arabic text-right"
                            )}>
                                {t('qualification.idealTitle')}
                            </h3>
                        </div>

                        <div 
                            className={cn(
                                "space-y-4",
                                isRTL && "text-right"
                            )}
                            dir={isRTL ? "rtl" : "ltr"}
                        >
                            {(Array.isArray(idealCandidates) ? idealCandidates : []).map((candidate, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.1 * index }}
                                    viewport={{ once: true }}
                                    className={cn(
                                        "relative w-full",
                                        isRTL ? "pr-8" : "pl-8"
                                    )}
                                >
                                    <CheckCircle className={cn(
                                        "w-5 h-5 text-green-600 dark:text-green-400 absolute mt-0.5",
                                        isRTL ? "right-0" : "left-0"
                                    )} />
                                    <span className={cn(
                                        "text-slate-600 dark:text-slate-300 leading-relaxed block",
                                        isRTL && "font-arabic text-right"
                                    )}>
                                        {candidate}
                                    </span>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-6 p-4 bg-green-100 dark:bg-green-900/30 rounded-xl border border-green-200 dark:border-green-800">
                            <p className={cn(
                                "text-green-800 dark:text-green-300 font-medium text-center",
                                isRTL && "font-arabic"
                            )}>
                                {t('qualification.idealNote')}
                            </p>
                        </div>
                    </motion.div>

                    {/* Considerations/Contraindications */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        viewport={{ once: true }}
                        className={cn(
                            "bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-8 border border-amber-200 dark:border-amber-800",
                            isRTL && "text-right"
                        )}
                    >
                        <div className={cn(
                            "relative mb-6",
                            isRTL ? "pr-16" : "pl-16"
                        )}>
                            <div className={cn(
                                "w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center absolute",
                                isRTL ? "right-0" : "left-0"
                            )}>
                                <XCircle className="w-6 h-6 text-white" />
                            </div>
                            <h3 className={cn(
                                "text-2xl font-bold text-amber-800 dark:text-amber-300",
                                isRTL && "font-arabic text-right"
                            )}>
                                {t('qualification.considerationsTitle')}
                            </h3>
                        </div>

                        <div 
                            className={cn(
                                "space-y-4",
                                isRTL && "text-right"
                            )}
                            dir={isRTL ? "rtl" : "ltr"}
                        >
                            {(Array.isArray(contraindications) ? contraindications : []).map((contraindication, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 0.1 * index }}
                                    viewport={{ once: true }}
                                    className={cn(
                                        "relative w-full",
                                        isRTL ? "pr-8" : "pl-8"
                                    )}
                                >
                                    <XCircle className={cn(
                                        "w-5 h-5 text-amber-600 dark:text-amber-400 absolute mt-0.5",
                                        isRTL ? "right-0" : "left-0"
                                    )} />
                                    <span className={cn(
                                        "text-slate-600 dark:text-slate-300 leading-relaxed block",
                                        isRTL && "font-arabic text-right"
                                    )}>
                                        {contraindication}
                                    </span>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-6 p-4 bg-amber-100 dark:bg-amber-900/30 rounded-xl border border-amber-200 dark:border-amber-800">
                            <p className={cn(
                                "text-amber-800 dark:text-amber-300 font-medium text-center",
                                isRTL && "font-arabic"
                            )}>
                                {t('qualification.considerationsNote')}
                            </p>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mt-16"
                >
                    <div className="bg-gold/10 rounded-2xl p-8 border border-gold/20 max-w-3xl mx-auto">
                        <h3 className={cn(
                            "text-2xl font-bold text-navy dark:text-white mb-4",
                            isRTL && "font-arabic"
                        )}>
                            {t('qualification.bottomCta.title')}
                        </h3>
                        <p className={cn(
                            "text-slate-600 dark:text-slate-300 text-lg",
                            isRTL && "font-arabic"
                        )}>
                            {t('qualification.bottomCta.description')}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
} 
